import { useQuery } from '@apollo/client';
import { Case, type Document, type User } from '@bybeam/platform-types';
import { Typography } from '@mui/material';
import DocumentList from '@platform-ui-common/components/DocumentList/index.js';
import Icon from '@platform-ui-common/components/Icon/index.js';
import Loading from '@platform-ui-common/components/Loading/index.js';
import Color, { BackgroundColors } from '@platform-ui-common/utilities/Color.js';
import type { TemporaryDocument } from '@platform-ui/@types/document.js';
import CaseDocumentUpload from '@platform-ui/case/CaseDocumentUpload/index.js';
import { Heading } from '@radix-ui/themes';
import cx from 'classnames';
import GetCaseDocuments from './GetCaseDocumentsQuery.graphql';

export const SYSTEM_GENERATED = 'System Generated';
export const getSystemGeneratedUploader = (doc: Document) =>
  ({
    name: `${doc.filename.split('.')[0]} - ${SYSTEM_GENERATED}`,
  }) as User;

interface CaseDocumentsProps {
  caseId: string;
}

export default function CaseDocuments({ caseId: id }: CaseDocumentsProps): JSX.Element {
  const {
    loading: caseDocumentsLoading,
    data: caseDocumentsData,
    refetch,
  } = useQuery<{ cases: { cases: Case[] } }, { id: string }>(GetCaseDocuments, {
    variables: { id },
  });

  const applicationDocuments =
    caseDocumentsData?.cases?.cases?.[0]?.applications?.[0]?.documents || [];
  const caseDocuments = caseDocumentsData?.cases?.cases?.[0]?.documents || [];

  const mappedApplicationDocuments = applicationDocuments.map<TemporaryDocument>(
    ({ id: docId, previewUrl: url, filename: name, ...rest }) => ({
      id: docId,
      url,
      name,
      permanent: rest.uploader.name.includes(SYSTEM_GENERATED) ?? false,
      ...rest,
    }),
  );

  const mappedCaseDocuments = caseDocuments.map<TemporaryDocument>(
    ({ id: docId, previewUrl: url, filename: name, ...rest }) => ({
      id: docId,
      url,
      name,
      permanent: rest.uploader.name.includes(SYSTEM_GENERATED) ?? false,
      ...rest,
    }),
  );

  if (caseDocumentsLoading) {
    return (
      <div>
        <Loading size="XL" />
      </div>
    );
  }

  return (
    <>
      <div
        className={cx(
          `${BackgroundColors[Color.LightBackground]} w-full py-4 px-6 md:px-8 rounded flex flex-col gap-3 grow`,
        )}
      >
        <Heading size="5" weight="regular">
          Application Documents
        </Heading>
        {mappedApplicationDocuments.length > 0 ? (
          <DocumentList documents={mappedApplicationDocuments} />
        ) : (
          <Typography>There are no documents associated with this application</Typography>
        )}
      </div>
      <div
        className={cx(
          `${
            BackgroundColors[Color.LightBackground]
          } w-full py-4 px-6 md:px-8 rounded flex flex-col gap-3 grow`,
        )}
      >
        <div className="flex gap-2 justify-between align-center">
          <Heading size="5" weight="regular">
            Additional Case Documents
          </Heading>
          <div className="min-w-0 inline-flex">
            <CaseDocumentUpload caseId={id} refetchData={refetch}>
              <Icon type="upload" size="MD" className="mr-2" />
              Upload Additional Documents
            </CaseDocumentUpload>
          </div>
        </div>
        {mappedCaseDocuments.length > 0 ? (
          <DocumentList documents={mappedCaseDocuments} />
        ) : (
          <Typography>There are no additional documents to display</Typography>
        )}
      </div>
    </>
  );
}
